/*import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [loggedIn, setLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL//"http://localhost:5000";

    const checkAuthStatus = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/check-auth`, {
                method: 'GET',
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setLoggedIn(data.loggedIn);
                setUser(data.user || null);
            } else {
                setLoggedIn(false);
                setUser(null);
            }
        } catch (err) {
            console.error('Auth check error:', err);
            setLoggedIn(false);
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            const response = await fetch(`${API_URL}/logout`, {
                method: 'POST',
                credentials: 'include',
            });

            if (response.ok) {
                setLoggedIn(false);
                setUser(null);
                return true;
            } else {
                console.error('Logout failed');
                return false;
            }
        } catch (err) {
            console.error('Logout error:', err);
            return false;
        }
    };

    const login = (userData) => {
        setLoggedIn(true);
        setUser(userData);
    };

    useEffect(() => {
        checkAuthStatus();
    }, []);

    const value = {
        loggedIn,
        loading,
        user,
        logout,
        login,
        checkAuthStatus
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
*/
// context/AuthContext.js
/*import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the context
const AuthContext = createContext();

// Custom hook to use the AuthContext
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
    const [loggedIn, setLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [accessToken, setAccessToken] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL;

    // Generic fetch function that handles token refresh
    const fetchWithAuth = async (url, options = {}) => {
        if (!options.headers) options.headers = {};
        if (accessToken) options.headers['Authorization'] = `Bearer ${accessToken}`;
        options.credentials = 'include'; // ensure refresh token cookie is sent

        let response = await fetch(url, options);

        // Retry if access token expired
        if (response.status === 401 || response.status === 403) {
            const refreshRes = await fetch(`${API_URL}/refresh-token`, {
                method: 'POST',
                credentials: 'include',
            });

            if (refreshRes.ok) {
                const data = await refreshRes.json();
                setAccessToken(data.accessToken);
                options.headers['Authorization'] = `Bearer ${data.accessToken}`;
                response = await fetch(url, options); // retry original request
            } else {
                setLoggedIn(false);
                setUser(null);
                setAccessToken(null);
            }
        }

        return response;
    };

    // Restore session on app load
    const restoreSession = async () => {
        try {
            setLoading(true);

            // 1. Try refreshing token
            const refreshRes = await fetch(`${API_URL}/refresh-token`, {
                method: 'POST',
                credentials: 'include', // send refresh token cookie
            });

            if (refreshRes.ok) {
                const { accessToken: newAccessToken } = await refreshRes.json();
                setAccessToken(newAccessToken);

                // 2. Check auth with new token
                const response = await fetch(`${API_URL}/check-auth`, {
                    headers: { Authorization: `Bearer ${newAccessToken}` },
                    credentials: 'include',
                });

                if (response.ok) {
                    const data = await response.json();
                    setLoggedIn(data.loggedIn);
                    setUser(data.user || null);
                } else {
                    setLoggedIn(false);
                    setUser(null);
                }
            } else {
                setLoggedIn(false);
                setUser(null);
            }
        } catch (err) {
            console.error('Session restore error:', err);
            setLoggedIn(false);
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    // Login function
    const login = (userData, token) => {
        setLoggedIn(true);
        setUser(userData);
        setAccessToken(token);
    };

    // Logout function
    const logout = async () => {
        try {
            const response = await fetch(`${API_URL}/logout`, {
                method: 'POST',
                credentials: 'include',
            });

            if (response.ok) {
                setLoggedIn(false);
                setUser(null);
                setAccessToken(null);
                return true;
            } else {
                console.error('Logout failed');
                return false;
            }
        } catch (err) {
            console.error('Logout error:', err);
            return false;
        }
    };

    // Run restore session on mount
    useEffect(() => {
        restoreSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = {
        loggedIn,
        loading,
        user,
        accessToken,
        login,
        logout,
        fetchWithAuth,
        checkAuthStatus: restoreSession, // optional alias
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
*/
// context/AuthContext.js
// context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    const [user, setUser] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [loggedIn, setLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);

    // Check persistent logout state
    const isLoggedOut = localStorage.getItem('loggedOut') === 'true';

    const fetchWithAuth = async (url, options = {}) => {
        if (!options.headers) options.headers = {};
        if (accessToken) options.headers['Authorization'] = `Bearer ${accessToken}`;
        options.credentials = 'include';

        let response = await fetch(url, options);

        if (response.status === 401 || response.status === 403) {
            const refreshRes = await fetch(`${API_URL}/refresh-token`, {
                method: 'POST',
                credentials: 'include',
            });

            if (refreshRes.ok) {
                const { accessToken: newAccessToken } = await refreshRes.json();
                setAccessToken(newAccessToken);
                options.headers['Authorization'] = `Bearer ${newAccessToken}`;
                response = await fetch(url, options);
            } else {
                await handleLogout();
            }
        }

        return response;
    };

    const restoreSession = useCallback(async () => {
        if (isLoggedOut) {
            setLoading(false);
            return; // Do not restore if user logged out
        }

        setLoading(true);
        try {
            const refreshRes = await fetch(`${API_URL}/refresh-token`, {
                method: 'POST',
                credentials: 'include',
            });

            if (refreshRes.ok) {
                const { accessToken: newAccessToken } = await refreshRes.json();
                setAccessToken(newAccessToken);

                const authRes = await fetch(`${API_URL}/check-auth`, {
                    headers: { Authorization: `Bearer ${newAccessToken}` },
                    credentials: 'include',
                });

                if (authRes.ok) {
                    const data = await authRes.json();
                    setUser(data.user || null);
                    setLoggedIn(data.loggedIn);
                } else {
                    await handleLogout();
                }
            } else {
                await handleLogout();
            }
        } catch (err) {
            console.error('Session restore error:', err);
            await handleLogout();
        } finally {
            setLoading(false);
        }
    }, [API_URL, isLoggedOut]);

    const login = (userData, token) => {
        setUser(userData);
        setAccessToken(token);
        setLoggedIn(true);
        localStorage.removeItem('loggedOut'); // clear logout state
    };

    const handleLogout = async (redirectCallback) => {
        try {
            await fetch(`${API_URL}/logout`, { method: 'POST', credentials: 'include' });
        } catch (err) {
            console.error('Logout request failed:', err);
        } finally {
            setUser(null);
            setAccessToken(null);
            setLoggedIn(false);
            localStorage.setItem('loggedOut', 'true'); // persist logout state
            if (redirectCallback) redirectCallback();
        }
    };

    useEffect(() => {
        restoreSession();
    }, [restoreSession]);

    const value = {
        user,
        accessToken,
        loggedIn,
        loading,
        login,
        logout: handleLogout,
        fetchWithAuth,
        restoreSession,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
